// Tenantra Inc. - Header for product sub-pages; mirrors the landing page header
import { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';

export const PRODUCT_LINKS = [
  { name: 'Rent Reporting', href: '/rent-reporting', desc: 'Rent payments reported to Equifax Canada' },
  { name: 'Rent Collection', href: '/rent-collection', desc: 'Bank debit and Interac, matched to the lease' },
  { name: 'Tenant Screening', href: '/screening', desc: 'ID verification and verified rent history', tag: 'Beta' },
];

export const RESOURCE_LINKS = [
  { name: 'FAQ', href: '/faq' },
  { name: 'Whitepaper', href: '/whitepaper' },
  { name: 'Ecosystem & Roadmap', href: '/community' },
  { name: 'Careers', href: '/careers' },
];

const OVERVIEW_LINKS = [
  { name: 'Why Tenantra', href: '/#problem' },
  { name: 'How It Works', href: '/#how-it-works' },
  { name: 'Landlords', href: '/#landlords' },
  { name: 'Tenants', href: '/#tenants' },
];

export const NavDropdown = ({ label, items, testId, active }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid={testId} className={`inline-flex items-center gap-1 text-sm font-medium transition-colors duration-300 py-4 ${active ? 'text-white' : 'text-white/85 hover:text-white'}`}>
        {label} <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full -mt-1 w-72 bg-white border border-stone-200 rounded-lg shadow-[0_12px_32px_-12px_rgba(15,23,42,0.25)] py-2" data-testid={`${testId}-menu`}>
          {items.map((it) => (
            <a key={it.href} href={it.href} className={`flex items-start justify-between gap-3 px-4 py-2.5 hover:bg-stone-50 transition-colors ${active && it.href === '/' + active ? 'bg-teal-50/60' : ''}`}>
              <span>
                <span className="block text-sm font-medium text-stone-900">{it.name}</span>
                {it.desc && <span className="block text-xs text-stone-500 mt-0.5">{it.desc}</span>}
              </span>
              {it.tag && <span className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-stone-500 border border-stone-300 rounded px-1.5 py-0.5">{it.tag}</span>}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

const MobileGroup = ({ title, items }) => (
  <>
    <p className="px-3 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500">{title}</p>
    {items.map((item) => (
      <a key={item.href} href={item.href} className="flex items-center justify-between text-base text-stone-700 hover:text-teal-700 hover:bg-teal-50 px-3 py-3 rounded-lg transition-colors duration-200" data-testid={`mobile-nav-${item.href.replace(/[/#]/g, '')}`}>
        {item.name}
        {item.tag && <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-stone-500">{item.tag}</span>}
      </a>
    ))}
  </>
);

export const PublicNav = ({ active = '' }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-teal-600 shadow-[0_2px_12px_-4px_rgba(13,148,136,0.5)]" data-testid="public-nav">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Logo size="md" dark data-testid="logo-link" />

          <nav className="hidden md:flex items-center gap-8">
            <NavDropdown label="Products" items={PRODUCT_LINKS} testId="nav-products" active={active} />
            {OVERVIEW_LINKS.map((item) => (
              <a key={item.name} href={item.href} className="text-sm font-medium text-white/85 hover:text-white transition-all duration-300 relative group">
                {item.name}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-white group-hover:w-full transition-all duration-300 rounded-full" />
              </a>
            ))}
            <NavDropdown label="Resources" items={RESOURCE_LINKS} testId="nav-resources" />
          </nav>

          <div className="hidden sm:flex items-center gap-3">
            <a href="/login" className="text-sm font-medium text-white/90 hover:text-white transition-colors duration-300 px-3 py-2" data-testid="login-btn">Log In</a>
            <a href="/register" data-testid="register-btn">
              <Button className="h-9 px-5 bg-white hover:bg-teal-50 text-teal-700 text-sm font-semibold transition-colors duration-200 rounded-lg shadow-sm">Get Started</Button>
            </a>
          </div>

          <button className="md:hidden p-2 text-white hover:text-white/80 transition-colors" aria-label="Toggle menu" data-testid="mobile-menu-btn" onClick={() => setOpen(!open)}>
            <div className="relative w-5 h-5">
              <span className={`absolute left-0 w-5 h-0.5 bg-current transition-all duration-300 ${open ? 'top-2 rotate-45' : 'top-1'}`} />
              <span className={`absolute left-0 top-2 w-5 h-0.5 bg-current transition-all duration-300 ${open ? 'opacity-0' : 'opacity-100'}`} />
              <span className={`absolute left-0 w-5 h-0.5 bg-current transition-all duration-300 ${open ? 'top-2 -rotate-45' : 'top-3'}`} />
            </div>
          </button>
        </div>
      </header>

      {open && (
        <div className="md:hidden fixed inset-x-0 top-14 bottom-0 z-40 bg-white border-t border-teal-700/30 overflow-y-auto" data-testid="mobile-menu-panel">
          <div className="px-4 py-5 space-y-1">
            <MobileGroup title="Products" items={PRODUCT_LINKS} />
            <MobileGroup title="Overview" items={OVERVIEW_LINKS} />
            <MobileGroup title="Resources" items={RESOURCE_LINKS} />
            <div className="pt-4 mt-3 border-t border-stone-200 grid grid-cols-2 gap-3">
              <a href="/login" className="h-11 inline-flex items-center justify-center rounded-lg border border-stone-300 bg-white text-sm font-medium text-stone-800" data-testid="mobile-login-btn">Log In</a>
              <a href="/register" className="h-11 inline-flex items-center justify-center rounded-lg bg-teal-600 text-sm font-medium text-white" data-testid="mobile-register-btn">Get Started</a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
